import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { UserPlus, FileDown, Edit, Banknote } from 'lucide-react';
import Layout from '@/components/Layout';
import { ContractDialog } from '@/components/ContractDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Íslenskir lífeyrissjóðir
const PENSION_FUNDS = [
  'Birta',
  'Gildi',
  'Lífeyrissjóður verslunarmanna',
  'Stapi',
  'Frjálsi',
  'LSR',
  'Söfnunarsjóður lífeyrisréttinda',
  'Annar sjóður'
];

// Íslensk stéttarfélög  
const UNIONS = [
  'Efling',
  'VR',
  'Eflingar-Stéttarfélag',
  'Framsýn',
  'Kjalar',
  'SSF',
  'Hlíf',
  'Starfsgreinasambandið',
  'BHM',
  'Landssamband lögreglumanna',
  'Annað félag'
];

// Vikuáætlun: 0=sun, 1=mán ... 6=lau (sama og backend)
const WEEKDAYS = [
  { v: '1', l: 'Mánudagur' },
  { v: '2', l: 'Þriðjudagur' },
  { v: '3', l: 'Miðvikudagur' },
  { v: '4', l: 'Fimmtudagur' },
  { v: '5', l: 'Föstudagur' },
  { v: '6', l: 'Laugardagur' },
  { v: '0', l: 'Sunnudagur' },
];

const defaultWeeklySchedule = () => ({
  '1': { enabled: true, start: '', end: '' },
  '2': { enabled: true, start: '', end: '' },
  '3': { enabled: true, start: '', end: '' },
  '4': { enabled: true, start: '', end: '' },
  '5': { enabled: true, start: '', end: '' },
  '6': { enabled: false, start: '', end: '' },
  '0': { enabled: false, start: '', end: '' },
});

const scheduleDayHours = (d) => {
  if (!d?.enabled || !d.start || !d.end) return null;
  const [h1, m1] = d.start.split(':').map(Number);
  const [h2, m2] = d.end.split(':').map(Number);
  let g = (h2 + m2 / 60) - (h1 + m1 / 60);
  if (g <= 0) g += 24;
  return Math.round(g * 100) / 100;
};

export default function Employees({ user, onLogout }) {
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [leavesToday, setLeavesToday] = useState({}); // key: user_id, value: leave_type
  const [sickStatus, setSickStatus] = useState({}); // key: employee_id, value: veikindastaða frá backend
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [wageTable, setWageTable] = useState({});
  const [wageYear, setWageYear] = useState('2026');
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [advanceEmployee, setAdvanceEmployee] = useState(null);
  const [advancePayments, setAdvancePayments] = useState([]);
  const [advanceForm, setAdvanceForm] = useState({ amount: '', date: '', note: '' });
  const [paydayLists, setPaydayLists] = useState({ pension_funds: [], unions: [] });
  const [contractEmployee, setContractEmployee] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    ssn: '',
    phone: '',
    address: '',
    position: '',
    workplace: '',
    employment_type: '',
    start_date: '',
    work_schedule: '',
    monthly_salary: '',
    day_rate: '',
    overtime_rate: '',
    launaflokkur: '',
    reynsla: '',
    wage_divisor: '173.33',
    union_name: '',
    pension_fund: '',
    pin: '',
    is_active: true,
    is_permanent: false,
    shift_start: '',
    shift_end: '',
    auto_clockout: false,
    auto_clockout_time: '',
    lunch_deduction_minutes: 30,
    car_allowance: '',
    pay_type: 'monthly',
    holiday_pay_eligible: true,
    weekly_hours: 40,
    work_days: '1,2,3,4,5',
    daily_work_hours: 8,
    sick_days_accrued: 0,
    vacation_days_accrued: 0,
    pay_method: '',
    rate_source: '',
    salary_basis: '',
    contract_monthly_salary: '',
    automatic_overtime_rate: true,
    wage_source_field: '',
    employment_percentage: 100,
    preferred_language: 'is',
    missing_attendance_notifications_enabled: true
  });

  const [weeklySchedule, setWeeklySchedule] = useState(defaultWeeklySchedule());

  const fetchPaydayLists = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/payday/reference-lists`, { headers: { Authorization: `Bearer ${token}` } });
      setPaydayLists(res.data);
    } catch { /* nota fallback lista */ }
  }, []);

  const fetchWageTable = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/wage-table`, { headers: { Authorization: `Bearer ${token}` } });
      setWageTable(res.data.tables || {});
    } catch (e) { console.error('Wage table fetch error:', e); }
  }, []);

  const applyWage = (flokkur, reynsla, year = wageYear) => {
    const key = reynsla === 'byrjunarlaun' ? 'byrjunarlaun' : reynsla === '1_ar' ? '1_ar' : reynsla === '3_ar' ? '3_ar' : '5_ar';
    const entry = (wageTable[year] || {})[flokkur];
    if (!entry) return;
    setFormData((prev) => ({
      ...prev,
      launaflokkur: flokkur,
      reynsla,
      monthly_salary: entry.monthly[key],
      day_rate: entry.dagvinna[key],
      overtime_rate: entry.yfirvinna[key],
    }));
  };

  const openAdvance = async (employee) => {
    setAdvanceEmployee(employee);
    setAdvanceForm({ amount: '', date: '', note: '' });
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/advance-payments/${employee.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdvancePayments(res.data || []);
    } catch { setAdvancePayments([]); }
    setAdvanceOpen(true);
  };

  const handleCreateAdvance = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/advance-payments`, {
        employee_id: advanceEmployee.id,
        amount: parseFloat(advanceForm.amount),
        date: advanceForm.date,
        note: advanceForm.note || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Fyrirframgreiðsla skráð!');
      setAdvanceForm({ amount: '', date: '', note: '' });
      // Refresh list
      const res = await axios.get(`${API}/advance-payments/${advanceEmployee.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdvancePayments(res.data || []);
    } catch (err) {
      toast.error('Villa við skráningu');
    }
  };

  const handleDeleteAdvance = async (paymentId) => {
    if (!window.confirm('Eyða þessari fyrirframgreiðslu?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/advance-payments/${paymentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdvancePayments((prev) => prev.filter((p) => p.id !== paymentId));
      toast.success('Eytt!');
    } catch { toast.error('Villa'); }
  };

  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter out any invalid data
      const validEmployees = response.data.filter(emp => 
        emp && typeof emp === 'object' && emp.id
      );
      
      setEmployees(validEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Villa við að sækja starfsmenn');
      setEmployees([]);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers([response.data]);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const fetchLeavesToday = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/leave/calendar`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const todayStr = new Date().toISOString().split('T')[0];
      const map = {};
      (response.data || []).forEach((ev) => {
        if (todayStr >= ev.start_date && todayStr <= ev.end_date) {
          map[ev.user_id] = ev.leave_type; // 'vacation' eða 'sick'
        }
      });
      setLeavesToday(map);
    } catch (error) {
      console.error('Error fetching today leaves:', error);
    }
  }, []);

  const fetchSickStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sick-leave/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const map = {};
      (response.data || []).forEach((st) => { map[st.employee_id] = st; });
      setSickStatus(map);
    } catch (error) {
      console.error('Error fetching sick leave status:', error);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
    fetchUsers();
    fetchSickStatus();
    fetchLeavesToday();
    fetchWageTable();
    fetchPaydayLists();
  }, [fetchEmployees, fetchUsers, fetchSickStatus, fetchLeavesToday, fetchWageTable, fetchPaydayLists]);

  const resetForm = () => {
    setFormData({
      full_name: '',
      email: '',
      ssn: '',
      phone: '',
      address: '',
      position: '',
      workplace: '',
      employment_type: '',
      start_date: '',
      work_schedule: '',
      monthly_salary: '',
      day_rate: '',
      overtime_rate: '',
      wage_divisor: '173.33',
      union_name: '',
      pension_fund: '',
      pin: '',
      is_active: true,
      is_permanent: false,
      shift_start: '',
      shift_end: '',
      sick_days_accrued: 0,
      vacation_days_accrued: 0,
      work_days: '1,2,3,4,5',
      pay_method: '',
      rate_source: '',
      salary_basis: '',
      contract_monthly_salary: '',
      automatic_overtime_rate: true,
      wage_source_field: '',
      employment_percentage: 100,
      preferred_language: 'is',
      missing_attendance_notifications_enabled: true
    });
    setWeeklySchedule(defaultWeeklySchedule());
    setEditMode(false);
    setCurrentEmployee(null);
  };

  const handleEdit = async (employee) => {
    setEditMode(true);
    setCurrentEmployee(employee);
    
    // Employee already has full_name and email from backend
    setFormData({
      full_name: employee.full_name || '',
      email: employee.email || '',
      user_id: employee.user_id || '',
      ssn: employee.ssn || '',
      phone: employee.phone || '',
      address: employee.address || '',
      position: employee.position || '',
      workplace: employee.workplace || '',
      employment_type: employee.employment_type || '',
      start_date: employee.start_date || '',
      work_schedule: employee.work_schedule || '',
      monthly_salary: employee.monthly_salary || '',
      day_rate: employee.day_rate || '',
      overtime_rate: employee.overtime_rate || '',
      launaflokkur: employee.launaflokkur || '',
      reynsla: employee.reynsla || '',
      wage_divisor: String(employee.wage_divisor ?? 173.33),
      union_name: employee.union_name || '',
      pension_fund: employee.pension_fund || '',
      pin: employee.pin || '',
      is_active: employee.is_active !== undefined ? employee.is_active : true,
      is_permanent: employee.is_permanent ?? false,
      shift_start: employee.shift_start || '',
      shift_end: employee.shift_end || '',
      auto_clockout: employee.auto_clockout ?? false,
      auto_clockout_time: employee.auto_clockout_time || '',
      lunch_deduction_minutes: employee.lunch_deduction_minutes ?? 30,
      car_allowance: employee.car_allowance || '',
      pay_type: employee.pay_type || 'monthly',
      holiday_pay_eligible: employee.holiday_pay_eligible ?? true,
      weekly_hours: employee.weekly_hours ?? 40,
      work_days: employee.work_days || '1,2,3,4,5',
      daily_work_hours: employee.daily_work_hours ?? 8,
      sick_days_accrued: employee.sick_days_accrued ?? 0,
      vacation_days_accrued: employee.vacation_days_accrued ?? 0,
      pay_method: employee.pay_method || (employee.salary_basis === 'monthly_fixed' ? 'monthly' : employee.salary_basis ? 'hourly' : ''),
      rate_source: employee.rate_source || (employee.salary_basis === 'wage_table' ? 'wage_table' : employee.salary_basis ? 'manual' : ''),
      salary_basis: employee.salary_basis || '',
      contract_monthly_salary: employee.contract_monthly_salary || '',
      automatic_overtime_rate: employee.automatic_overtime_rate ?? true,
      wage_source_field: employee.wage_source_field || '',
      employment_percentage: employee.employment_percentage ?? 100,
      preferred_language: employee.preferred_language || 'is',
      missing_attendance_notifications_enabled: employee.missing_attendance_notifications_enabled !== false
    });

    // Vikuáætlun: nota vistaða áætlun, annars leiða út frá work_days + vaktartímum
    const base = defaultWeeklySchedule();
    const ws = employee.weekly_schedule;
    if (ws && Object.keys(ws).length) {
      Object.keys(base).forEach((k) => {
        const day = ws[k];
        base[k] = day
          ? { enabled: true, start: day.start || '', end: day.end || '' }
          : { enabled: false, start: '', end: '' };
      });
    } else {
      const days = (employee.work_days || '1,2,3,4,5').split(',').filter(Boolean);
      Object.keys(base).forEach((k) => {
        base[k] = {
          enabled: days.includes(k),
          start: days.includes(k) ? (employee.shift_start || '') : '',
          end: days.includes(k) ? (employee.shift_end || '') : '',
        };
      });
    }
    setWeeklySchedule(base);

    setOpen(true);
  };

  const recalcRates = async (sourceField) => {
    const pm = formData.pay_method;
    const rs = formData.rate_source || 'wage_table';
    if (!pm || rs !== 'manual') return;
    const src = sourceField === 'monthly_salary' ? 'monthly_salary' : 'day_rate';
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/wage-rates/preview`, {
        pay_method: pm,
        rate_source: 'manual',
        wage_divisor: parseFloat(formData.wage_divisor) || 173.33,
        automatic_overtime_rate: formData.automatic_overtime_rate ?? true,
        overtime_rate: formData.overtime_rate || null,
        day_rate: formData.day_rate || null,
        monthly_salary: formData.monthly_salary || null,
        contract_monthly_salary: formData.contract_monthly_salary || null,
        wage_source_field: pm === 'hourly' ? src : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const r = res.data;
      setFormData((prev) => ({
        ...prev,
        wage_source_field: pm === 'hourly' ? src : prev.wage_source_field,
        monthly_salary: r.monthly_salary ?? prev.monthly_salary,
        day_rate: r.day_rate ?? prev.day_rate,
        contract_monthly_salary: pm === 'monthly' ? (r.contract_monthly_salary ?? prev.contract_monthly_salary) : prev.contract_monthly_salary,
        overtime_rate: (prev.automatic_overtime_rate ?? true) ? (r.overtime_rate ?? prev.overtime_rate) : prev.overtime_rate,
      }));
    } catch { /* backend reiknar endanlega við vistun */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        launaflokkur: formData.launaflokkur || null,
        reynsla: formData.reynsla || null,
        monthly_salary: formData.monthly_salary || null,
        day_rate: formData.day_rate || null,
        overtime_rate: formData.overtime_rate || null,
        car_allowance: formData.car_allowance || null,
        lunch_deduction_minutes: parseInt(formData.lunch_deduction_minutes) || 0,
        pay_type: formData.pay_type || 'monthly',
        holiday_pay_eligible: formData.holiday_pay_eligible,
        weekly_hours: parseFloat(formData.weekly_hours) || 40,
        work_days: formData.work_days || '1,2,3,4,5',
        daily_work_hours: parseFloat(formData.daily_work_hours) || 8,
        wage_divisor: parseFloat(formData.wage_divisor) || 173.33,
        pay_method: formData.pay_method || null,
        rate_source: formData.pay_method ? (formData.rate_source || 'wage_table') : null,
        salary_basis: formData.salary_basis || null,
        contract_monthly_salary: formData.contract_monthly_salary === '' || formData.contract_monthly_salary == null ? null : Number(formData.contract_monthly_salary),
        automatic_overtime_rate: formData.automatic_overtime_rate ?? true,
        wage_source_field: formData.wage_source_field || null,
        employment_percentage: parseFloat(formData.employment_percentage) || 100,
        preferred_language: formData.preferred_language || 'is',
        missing_attendance_notifications_enabled: formData.missing_attendance_notifications_enabled !== false,
      };

      // Vikuáætlun: aðeins vaktardagar fara í payload, work_days leitt út frá henni
      const wsPayload = {};
      Object.entries(weeklySchedule).forEach(([k, v]) => {
        if (v.enabled) wsPayload[k] = { start: v.start || null, end: v.end || null };
      });
      payload.weekly_schedule = Object.keys(wsPayload).length ? wsPayload : null;
      if (Object.keys(wsPayload).length) {
        payload.work_days = Object.keys(wsPayload).sort().join(',');
      }
      
      let paydaySync = null;
      if (editMode && currentEmployee) {
        const res = await axios.put(`${API}/employees/${currentEmployee.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        paydaySync = res.data?.payday_sync;
        toast.success('Starfsmaður uppfærður!');
      } else {
        const res = await axios.post(`${API}/employees`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        paydaySync = res.data?.payday_sync;
        toast.success('Starfsmaður bætt við!');
      }
      if (paydaySync?.status === 'ok') {
        const actionText = { created: 'stofnaður í Payday', updated: 'uppfærður í Payday', deactivated: 'gerður óvirkur í Payday' }[paydaySync.action] || 'samstilltur við Payday';
        toast.success(`Sjálfkrafa ${actionText} ✓`);
      } else if (paydaySync?.status === 'error') {
        toast.warning('Payday samstilling mistókst – starfsmaðurinn er vistaður hér. Hægt er að samstilla handvirkt í Stillingum.');
      }
      
      setOpen(false);
      fetchEmployees();
      fetchSickStatus();
      resetForm();
    } catch (error) {
      console.error('Submit error:', error);
      
      // Handle validation errors properly
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors
          const errorMsg = error.response.data.detail
            .map(err => `${err.loc?.join('.')}: ${err.msg}`)
            .join(', ');
          toast.error(errorMsg);
        } else if (typeof error.response.data.detail === 'string') {
          toast.error(error.response.data.detail);
        } else {
          toast.error('Villa í gögnum');
        }
      } else {
        toast.error('Villa kom upp');
      }
    }
  };

  const downloadContract = async (employeeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/contract/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'radningarsamningur.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Samningur sóttur!');
    } catch (error) {
      toast.error('Villa við að sækja samning');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-8" data-testid="employees-page">
        <div className="flex justify-between items-center">
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Work Sans' }}>Starfsmenn</h1>
          <div className="flex gap-3">
            <Input
              placeholder="Leita..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-48"
              data-testid="employee-search"
            />
            <Button
              variant={showInactive ? 'outline' : 'default'}
              onClick={() => setShowInactive(false)}
              data-testid="show-active-employees"
            >
              Virkir starfsmenn
            </Button>
            <Button
              variant={showInactive ? 'default' : 'outline'}
              onClick={() => setShowInactive(true)}
              data-testid="show-inactive-employees"
            >
              Óvirkir starfsmenn
            </Button>
          </div>
        </div>
        
        <Dialog open={open} onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-blue-500 to-purple-600" data-testid="add-employee-button">
              <UserPlus className="mr-2 h-4 w-4" />
              Bæta við starfsmanni
            </Button>
          </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editMode ? 'Breyta starfsmanni' : 'Nýr starfsmaður'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Grunnupplýsingar */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Grunnupplýsingar</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {editMode ? (
                      <>
                        <div className="space-y-2">
                          <Label>Fullt nafn</Label>
                          <Input
                            value={formData.full_name}
                            disabled
                            className="bg-gray-100"
                            data-testid="fullname-display"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Netfang</Label>
                          <Input
                            value={formData.email}
                            disabled
                            className="bg-gray-100"
                            data-testid="email-display"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <Label>Fullt nafn</Label>
                          <Input
                            value={formData.full_name}
                            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                            required
                            placeholder="Jón Jónsson"
                            data-testid="fullname-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Netfang</Label>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            placeholder="jon@example.is"
                            data-testid="email-input"
                          />
                        </div>
                      </>
                    )}
                    <div className="space-y-2">
                      <Label>Kennitala</Label>
                      <Input
                        value={formData.ssn}
                        onChange={(e) => setFormData({ ...formData, ssn: e.target.value })}
                        required
                        data-testid="ssn-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Símanúmer</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        data-testid="phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Heimilisfang</Label>
                      <Input
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        required
                        data-testid="address-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Starfsupplýsingar */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Starfsupplýsingar</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Starfsheiti</Label>
                      <Input
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                        required
                        placeholder="t.d. Sölufulltrúi"
                        data-testid="position-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Starfsstaður</Label>
                      <Input
                        value={formData.workplace}
                        onChange={(e) => setFormData({ ...formData, workplace: e.target.value })}
                        placeholder="t.d. Reykjavík"
                        data-testid="workplace-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ráðningarform</Label>
                      <select
                        value={formData.employment_type}
                        onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        data-testid="employment-type-select"
                      >
                        <option value="">Veldu ráðningarform...</option>
                        {formData.employment_type && !['permanent', 'fixed_term'].includes(formData.employment_type) && (
                          <option value={formData.employment_type}>{formData.employment_type} (eldra gildi)</option>
                        )}
                        <option value="permanent">Ótímabundin ráðning (fastráðinn)</option>
                        <option value="fixed_term">Tímabundin ráðning</option>
                      </select>
                      <p className="text-xs text-gray-500">Ráðningarform er óháð launaformi – fastráðinn getur verið á tímakaupi eða mánaðarlaunum.</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Fyrsti starfsdagur</Label>
                      <Input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        required
                        data-testid="start-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tungumál / Preferred language</Label>
                      <select
                        value={formData.preferred_language || 'is'}
                        onChange={(e) => setFormData({ ...formData, preferred_language: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        data-testid="preferred-language-select"
                      >
                        <option value="is">Íslenska</option>
                        <option value="en">English</option>
                      </select>
                    </div>
                    <div className="col-span-2 flex items-center justify-between border rounded-md px-3 py-2">
                      <div>
                        <Label>Sjálfvirkar mætingartilkynningar</Label>
                        <p className="text-xs text-gray-500">Tölvupóstur ef ekki er stimplað inn á réttum tíma. Mætingareftirlit á Yfirliti helst óbreytt.</p>
                      </div>
                      <Switch
                        checked={formData.missing_attendance_notifications_enabled !== false}
                        onCheckedChange={(v) => setFormData({ ...formData, missing_attendance_notifications_enabled: v })}
                        data-testid="employee-notifications-toggle"
                      />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label>Vinnutími</Label>
                      <Input
                        value={formData.work_schedule}
                        onChange={(e) => setFormData({ ...formData, work_schedule: e.target.value })}
                        placeholder="t.d. Mán-Fös 9:00-17:00"
                        data-testid="work-schedule-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Launakjör */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold">Launakjör</h3>
                    <div className="flex rounded-md border overflow-hidden text-sm" data-testid="wage-year-toggle">
                      {['2026', '2027'].map((y) => (
                        <button
                          key={y}
                          type="button"
                          onClick={() => {
                            setWageYear(y);
                            if (formData.launaflokkur && formData.reynsla) applyWage(formData.launaflokkur, formData.reynsla, y);
                          }}
                          className={`px-3 py-1.5 ${wageYear === y ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                          data-testid={`wage-year-${y}`}
                        >
                          Taxtar {y}
                        </button>
                      ))}
                    </div>
                  </div>
                  {wageYear === '2027' && (
                    <p className="text-xs text-amber-600 mb-2" data-testid="wage-year-2027-note">
                      Taxtar frá 1. jan 2027: +3,5% eða að lágmarki 23.750 kr. á mánaðarlaun (SA/Efling).
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Launaform</Label>
                      <select
                        value={formData.pay_method}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFormData({ ...formData, pay_method: v, rate_source: v ? (formData.rate_source || 'wage_table') : '' });
                        }}
                        className="w-full px-3 py-2 border rounded-md"
                        data-testid="pay-method-select"
                      >
                        <option value="">Eldra fyrirkomulag (óbreytt)</option>
                        <option value="hourly">Tímakaup</option>
                        <option value="monthly">Mánaðarlaun</option>
                      </select>
                      <p className="text-xs text-gray-500">Launaform er óháð ráðningarformi.</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Uppruni launa</Label>
                      <select
                        value={formData.pay_method ? (formData.rate_source || 'wage_table') : ''}
                        onChange={(e) => setFormData({ ...formData, rate_source: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        disabled={!formData.pay_method}
                        data-testid="rate-source-select"
                      >
                        {!formData.pay_method && <option value="">–</option>}
                        <option value="wage_table">Launatafla (launaflokkur + reynsla)</option>
                        <option value="manual">Sérsamin laun</option>
                      </select>
                    </div>
                    {(!formData.pay_method || formData.rate_source === 'wage_table') && (
                      <>
                    <div className="space-y-2">
                      <Label>Launaflokkur</Label>
                      <select
                        value={formData.launaflokkur}
                        onChange={(e) => {
                          const val = e.target.value ? parseInt(e.target.value) : '';
                          if (val && formData.reynsla) applyWage(val, formData.reynsla);
                          else setFormData({ ...formData, launaflokkur: val });
                        }}
                        className="w-full px-3 py-2 border rounded-md"
                        data-testid="launaflokkur-select"
                      >
                        <option value="">Veldu launaflokk...</option>
                        {Array.from({ length: 21 }, (_, i) => i + 4).map((n) => (
                          <option key={n} value={n}>Flokkur {n}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Reynsla</Label>
                      <select
                        value={formData.reynsla}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val && formData.launaflokkur) applyWage(formData.launaflokkur, val);
                          else setFormData({ ...formData, reynsla: val });
                        }}
                        className="w-full px-3 py-2 border rounded-md"
                        data-testid="reynsla-select"
                      >
                        <option value="">Veldu reynslu...</option>
                        <option value="byrjunarlaun">Byrjunarlaun</option>
                        <option value="1_ar">1 ár</option>
                        <option value="3_ar">3 ár</option>
                        <option value="5_ar">5 ár</option>
                      </select>
                    </div>
                      </>
                    )}
                    {formData.pay_method === 'monthly' && (formData.rate_source || 'wage_table') === 'manual' && (
                      <div className="space-y-2">
                        <Label>Umsamin mánaðarlaun (kr)</Label>
                        <Input
                          type="number"
                          value={formData.contract_monthly_salary}
                          onChange={(e) => setFormData({ ...formData, contract_monthly_salary: e.target.value })}
                          onBlur={() => recalcRates('contract_monthly_salary')}
                          data-testid="contract-monthly-input"
                        />
                        <p className="text-xs text-gray-500">
                          Umsaminn launagrunnur – breytist aldrei sjálfkrafa. Greiðsluhlutfall hverrar launakeyrslu reiknast sérstaklega.
                        </p>
                      </div>
                    )}
                    {formData.pay_method === 'monthly' && (
                      <div className="space-y-2">
                        <Label>Starfshlutfall (%)</Label>
                        <Input
                          type="number"
                          value={formData.employment_percentage}
                          onChange={(e) => setFormData({ ...formData, employment_percentage: e.target.value })}
                          data-testid="employment-percentage-input"
                        />
                      </div>
                    )}
                    {!(formData.pay_method === 'monthly' && (formData.rate_source || 'wage_table') === 'manual') && (
                    <div className="space-y-2">
                      <Label>
                        Mánaðarlaun (kr)
                        {formData.pay_method === 'hourly' && (
                          <span className="text-xs text-blue-600 ml-1">
                            {formData.rate_source === 'manual' && formData.wage_source_field === 'monthly_salary' ? '' : '(reiknað úr tímakaupi)'}
                          </span>
                        )}
                      </Label>
                      <Input
                        type="number"
                        value={formData.monthly_salary}
                        onChange={(e) => setFormData({
                          ...formData,
                          monthly_salary: e.target.value,
                          wage_source_field: formData.pay_method === 'hourly' && formData.rate_source === 'manual' ? 'monthly_salary' : formData.wage_source_field,
                        })}
                        onBlur={() => formData.pay_method === 'hourly' && formData.rate_source === 'manual' && recalcRates('monthly_salary')}
                        className={formData.launaflokkur && formData.reynsla ? 'bg-green-50 border-green-300' : ''}
                        data-testid="monthly-salary-input"
                      />
                      {formData.pay_method === 'hourly' && (
                        <p className="text-xs text-gray-500">Reiknuð mánaðarsamsvörun tímakaups (dagvinna × deilitala) – birtist á ráðningarsamningi.</p>
                      )}
                    </div>
                    )}
                    <div className="space-y-2">
                      <Label>
                        Dagvinnutímakaup (kr)
                        {(formData.pay_method === 'monthly' || (formData.pay_method === 'hourly' && formData.rate_source === 'manual' && formData.wage_source_field === 'monthly_salary')) && (
                          <span className="text-xs text-blue-600 ml-1">(sjálfreiknað)</span>
                        )}
                      </Label>
                      <Input
                        type="number"
                        value={formData.day_rate}
                        onChange={(e) => setFormData({
                          ...formData,
                          day_rate: e.target.value,
                          wage_source_field: formData.pay_method === 'hourly' && formData.rate_source === 'manual' ? 'day_rate' : formData.wage_source_field,
                        })}
                        onBlur={() => formData.pay_method === 'hourly' && formData.rate_source === 'manual' && recalcRates('day_rate')}
                        disabled={formData.pay_method === 'monthly'}
                        className={formData.launaflokkur && formData.reynsla ? 'bg-green-50 border-green-300' : ''}
                        data-testid="day-rate-input"
                      />
                      {formData.pay_method === 'monthly' && (
                        <p className="text-xs text-gray-500">Reiknað: mánaðarlaun ÷ deilitala.</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>
                        Yfirvinna (kr)
                        {formData.pay_method && (formData.automatic_overtime_rate ?? true) && (
                          <span className="text-xs text-blue-600 ml-1">(sjálfreiknað)</span>
                        )}
                      </Label>
                      <Input
                        type="number"
                        value={formData.overtime_rate}
                        onChange={(e) => setFormData({ ...formData, overtime_rate: e.target.value })}
                        disabled={!!formData.pay_method && (formData.automatic_overtime_rate ?? true)}
                        className={formData.launaflokkur && formData.reynsla ? 'bg-green-50 border-green-300' : ''}
                        data-testid="overtime-rate-input"
                      />
                      {formData.pay_method && (
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.automatic_overtime_rate ?? true}
                            onChange={(e) => setFormData({ ...formData, automatic_overtime_rate: e.target.checked })}
                            data-testid="auto-overtime-checkbox"
                          />
                          Yfirvinna reiknast sjálfkrafa skv. kjarasamningi (1,0385% af mánaðarlaunum)
                        </label>
                      )}
                      {formData.pay_method && !(formData.automatic_overtime_rate ?? true) && (
                        <p className="text-xs text-amber-600" data-testid="manual-overtime-warning">
                          Yfirvinnukaup eru handvirkt yfirskrifuð – uppfærast ekki þótt dagvinnukaup breytist (audit-skráð).
                        </p>
                      )}
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label>Kjarasamningur – deilitala dagvinnutímakaups</Label>
                      <select
                        value={formData.wage_divisor}
                        onChange={(e) => setFormData({ ...formData, wage_divisor: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        data-testid="wage-divisor-select"
                      >
                        <option value="173.33">SA/Efling almennt – 173,33 (sjálfgefið)</option>
                        <option value="156">Sérsamningur með deilitölu 156 (t.d. ákveðnir iðnaðarmannasamningar)</option>
                      </select>
                      <p className="text-xs text-gray-500">
                        Dagvinnutímakaup = mánaðarlaun ÷ deilitala (reiknast sjálfkrafa við vistun ef dagvinnureitur er tómur).
                        Deilitalan breytist EKKI þótt vikulegur vinnutími sé 36 klst vegna styttingar vinnuviku.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Stéttarfélag og Lífeyrissjóður */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Stéttarfélag og Lífeyrissjóður</h3>
                  {paydayLists.unions.length > 0 && (
                    <p className="text-xs text-gray-500 mb-2">Listar sóttir frá Payday – nöfnin passa við launakerfið.</p>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Stéttarfélag</Label>
                      <select
                        value={formData.union_name}
                        onChange={(e) => setFormData({ ...formData, union_name: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        required
                        data-testid="union-select"
                      >
                        <option value="">Veldu stéttarfélag</option>
                        {formData.union_name && !(paydayLists.unions.length > 0 ? paydayLists.unions : UNIONS).includes(formData.union_name) && (
                          <option value={formData.union_name}>{formData.union_name}</option>
                        )}
                        {Array.from(new Set(paydayLists.unions.length > 0 ? paydayLists.unions : UNIONS)).map((union) => (
                          <option key={union} value={union}>{union}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Lífeyrissjóður</Label>
                      <select
                        value={formData.pension_fund}
                        onChange={(e) => setFormData({ ...formData, pension_fund: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        required
                        data-testid="pension-select"
                      >
                        <option value="">Veldu lífeyrissjóð</option>
                        {formData.pension_fund && !(paydayLists.pension_funds.length > 0 ? paydayLists.pension_funds : PENSION_FUNDS).includes(formData.pension_fund) && (
                          <option value={formData.pension_fund}>{formData.pension_fund}</option>
                        )}
                        {Array.from(new Set(paydayLists.pension_funds.length > 0 ? paydayLists.pension_funds : PENSION_FUNDS)).map((fund) => (
                          <option key={fund} value={fund}>{fund}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* PIN og Dagar */}
                {/* Ráðning og helgidagar */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Ráðning og helgidagar</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Helgidagaflokkun</Label>
                      <select
                        value={formData.pay_type}
                        onChange={(e) => setFormData({ ...formData, pay_type: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        data-testid="pay-type-select"
                      >
                        <option value="monthly">Mánaðarlaun</option>
                        <option value="hourly_permanent">Fastráðinn tímakaup</option>
                        <option value="hourly_casual">Tilfallandi starfsmaður</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Daglegur vinnutími (klst)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.daily_work_hours}
                        onChange={(e) => setFormData({ ...formData, daily_work_hours: e.target.value })}
                        data-testid="daily-hours-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Vikulegur vinnutími (klst)</Label>
                      <Input
                        type="number"
                        value={formData.weekly_hours}
                        onChange={(e) => setFormData({ ...formData, weekly_hours: e.target.value })}
                        data-testid="weekly-hours-input"
                      />
                      {parseFloat(formData.weekly_hours) < 40 && (
                        <p className="text-xs text-amber-600" data-testid="weekly-cap-note">
                          Stytting vinnuviku: dagvinna umfram {formData.weekly_hours} klst á viku flokkast sjálfkrafa sem yfirvinna. Deilitala tímakaups helst óbreytt (173,33 nema annar samningur).
                        </p>
                      )}
                    </div>
                    <div className="space-y-2 col-span-2">
                      <div className="flex items-center justify-between">
                        <Label>Vinnuáætlun vikunnar</Label>
                        <button
                          type="button"
                          className="text-xs text-blue-600 hover:underline"
                          data-testid="schedule-copy-btn"
                          onClick={() => {
                            const mon = weeklySchedule['1'];
                            setWeeklySchedule((prev) => {
                              const next = { ...prev };
                              Object.keys(next).forEach((k) => {
                                if (k !== '1' && next[k].enabled) next[k] = { ...next[k], start: mon.start, end: mon.end };
                              });
                              return next;
                            });
                          }}
                        >
                          Afrita tíma mánudags á aðra vaktardaga
                        </button>
                      </div>
                      <div className="border rounded-lg divide-y" data-testid="weekly-schedule-table">
                        {WEEKDAYS.map(({ v, l }) => {
                          const d = weeklySchedule[v];
                          const hrs = scheduleDayHours(d);
                          return (
                            <div key={v} className="flex items-center gap-3 px-3 py-2">
                              <input
                                type="checkbox"
                                checked={d.enabled}
                                className="w-4 h-4"
                                data-testid={`schedule-day-${v}-toggle`}
                                onChange={(e) => setWeeklySchedule((prev) => ({ ...prev, [v]: { ...prev[v], enabled: e.target.checked } }))}
                              />
                              <span className="w-28 text-sm font-medium">{l}</span>
                              {d.enabled ? (
                                <>
                                  <Input
                                    type="time"
                                    className="w-32"
                                    value={d.start}
                                    data-testid={`schedule-day-${v}-start`}
                                    onChange={(e) => setWeeklySchedule((prev) => ({ ...prev, [v]: { ...prev[v], start: e.target.value } }))}
                                  />
                                  <span className="text-gray-400">–</span>
                                  <Input
                                    type="time"
                                    className="w-32"
                                    value={d.end}
                                    data-testid={`schedule-day-${v}-end`}
                                    onChange={(e) => setWeeklySchedule((prev) => ({ ...prev, [v]: { ...prev[v], end: e.target.value } }))}
                                  />
                                  {hrs != null && (
                                    <span className="text-xs text-gray-500 ml-auto">{hrs.toLocaleString('is-IS')} klst</span>
                                  )}
                                </>
                              ) : (
                                <span className="text-sm text-gray-400">Frí</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500" data-testid="schedule-week-total">
                        Vaktartímar vikunnar samtals: {Object.values(weeklySchedule).reduce((s, d) => s + (scheduleDayHours(d) || 0), 0).toLocaleString('is-IS')} klst (án frádráttar matarhlés). Tímarnir stýra mætingareftirliti, námundun stimplana og áætluðum stundum.
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="holiday_pay_eligible"
                        checked={formData.holiday_pay_eligible}
                        onChange={(e) => setFormData({ ...formData, holiday_pay_eligible: e.target.checked })}
                        className="w-4 h-4"
                        data-testid="holiday-pay-checkbox"
                      />
                      <Label htmlFor="holiday_pay_eligible" className="cursor-pointer">
                        Greiddir helgidagar
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Annað */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Annað</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-3 flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="is_permanent"
                        checked={formData.is_permanent}
                        onChange={(e) => setFormData({ ...formData, is_permanent: e.target.checked })}
                        className="w-4 h-4"
                        data-testid="is-permanent-checkbox"
                      />
                      <Label htmlFor="is_permanent" className="cursor-pointer">
                        Fastráðinn starfsmaður (samningsbundnir frídagar á rauðum dögum)
                      </Label>
                    </div>
                    <div className="col-span-3 flex items-center gap-4 mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <input
                        type="checkbox"
                        id="auto_clockout"
                        checked={formData.auto_clockout}
                        onChange={(e) => setFormData({ ...formData, auto_clockout: e.target.checked })}
                        className="w-4 h-4"
                        data-testid="auto-clockout-checkbox"
                      />
                      <Label htmlFor="auto_clockout" className="cursor-pointer flex-1">
                        Sjálfvirk útstimplun ef innskráður eftir miðnætti
                      </Label>
                      {formData.auto_clockout && (
                        <div className="flex items-center gap-2">
                          <Label className="text-sm whitespace-nowrap">Stimpla út kl.</Label>
                          <Input
                            type="time"
                            value={formData.auto_clockout_time}
                            onChange={(e) => setFormData({ ...formData, auto_clockout_time: e.target.value })}
                            className="w-28"
                            data-testid="auto-clockout-time"
                          />
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Matarhlé (mínútur)</Label>
                      <select
                        value={formData.lunch_deduction_minutes}
                        onChange={(e) => setFormData({ ...formData, lunch_deduction_minutes: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border rounded-md"
                        data-testid="lunch-minutes-select"
                      >
                        <option value={0}>Ekkert matarhlé</option>
                        <option value={15}>15 mín</option>
                        <option value={30}>30 mín</option>
                        <option value={45}>45 mín</option>
                        <option value={60}>60 mín</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Ökutækjastyrkur (kr/mán)</Label>
                      <Input
                        type="number"
                        value={formData.car_allowance}
                        onChange={(e) => setFormData({ ...formData, car_allowance: e.target.value })}
                        placeholder="0"
                        data-testid="car-allowance-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>PIN (4 tölustafir fyrir kiosk)</Label>
                      <Input
                        type="text"
                        maxLength="4"
                        pattern="\d{4}"
                        value={formData.pin}
                        onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, '') })}
                        placeholder="1234"
                        data-testid="pin-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Orlofsdagar uppsafnaðir</Label>
                      <Input
                        type="number"
                        value={formData.vacation_days_accrued}
                        onChange={(e) => setFormData({ ...formData, vacation_days_accrued: parseInt(e.target.value) || 0 })}
                        data-testid="vacation-days-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="is-active-checkbox"
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">
                    Virkur starfsmaður
                  </Label>
                </div>

                <Button type="submit" className="w-full mt-4" data-testid="submit-employee-button">
                  {editMode ? 'Uppfæra' : 'Bæta við'}
                </Button>
              </form>
            </DialogContent>
        </Dialog>

        <div className="grid gap-4">
          {employees
            .filter(emp => showInactive ? emp.is_active === false : emp.is_active !== false)
            .filter(emp => {
              if (!searchQuery) return true;
              const q = searchQuery.toLowerCase();
              return (emp.full_name || '').toLowerCase().includes(q)
                || (emp.ssn || '').includes(q)
                || (emp.phone || '').includes(q)
                || (emp.email || '').toLowerCase().includes(q);
            })
            .map((employee) => {
              if (!employee || !employee.id) return null;
              
              const isInactive = employee.is_active === false;
              const leaveTypeToday = leavesToday[employee.user_id];
              const leaveLabel =
                leaveTypeToday === 'vacation'
                  ? 'Í orlofi í dag'
                  : leaveTypeToday === 'sick'
                    ? 'Veikur í dag'
                    : null;

              // Veikindastaða frá backend (miðlægur source-of-truth útreikningur)
              const sickSt = sickStatus[employee.id];

              // Launaform/uppruni (nýtt líkan með eldra salary_basis fallback)
              const empPm = employee.pay_method || (employee.salary_basis === 'monthly_fixed' ? 'monthly' : employee.salary_basis ? 'hourly' : '');
              const empRs = employee.rate_source || (employee.salary_basis === 'wage_table' ? 'wage_table' : employee.salary_basis ? 'manual' : '');
              const kr = (v) => (v == null || v === '' ? '–' : `${Math.round(Number(v)).toLocaleString('is-IS')} kr.`);

              return (
                <Card key={employee.id} className={`shadow-md hover:shadow-lg transition-shadow ${isInactive ? 'border-red-200 opacity-70' : ''}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl mb-1">{employee.full_name || 'Nafn vantar'}</CardTitle>
                        <p className="text-sm text-gray-600">{employee.position}</p>
                        {leaveLabel && (
                          <span className={`inline-block mt-1 text-xs px-2 py-1 rounded-full ${
                            leaveTypeToday === 'vacation'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {leaveLabel}
                          </span>
                        )}
                        {isInactive && <span className="block text-sm text-red-500">Óvirkur</span>}
                      </div>
                      <Button
                        onClick={() => handleEdit(employee)}
                        variant="outline"
                        size="sm"
                        data-testid={`edit-employee-${employee.id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Breyta
                      </Button>
                    </div>
                  </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Kennitala</p>
                    <p className="font-medium">{employee.ssn}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Símanúmer</p>
                    <p className="font-medium">{employee.phone}</p>
                  </div>
                  {employee.email && (
                    <div>
                      <p className="text-gray-500">Netfang</p>
                      <p className="font-medium">{employee.email}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-500">Veikindadagar eftir</p>
                    {sickSt ? (
                      <>
                        <p
                          className={`font-medium ${sickSt.remaining === 0 ? 'text-red-600' : ''}`}
                          data-testid={`sick-remaining-${employee.id}`}
                        >
                          {sickSt.remaining}
                        </p>
                        <p className="text-xs text-gray-400">
                          Áunnið: {sickSt.entitlement_days} · Greitt: {sickSt.paid_used}
                        </p>
                        {sickSt.unpaid_days_total > 0 && (
                          <p className="text-xs text-amber-600" data-testid={`sick-unpaid-${employee.id}`}>
                            Ógreidd veikindi: {sickSt.unpaid_days_total} dagar
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="font-medium">–</p>
                    )}
                  </div>
                </div>
                <div className="mt-3 border-t pt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-testid={`wage-info-${employee.id}`}>
                  <div>
                    <p className="text-gray-500">Launaform</p>
                    <p className="font-medium" data-testid={`pay-method-label-${employee.id}`}>
                      {empPm === 'monthly' ? 'Mánaðarlaun' : empPm === 'hourly' ? 'Tímakaup' : 'Eldra fyrirkomulag'}
                    </p>
                    {empPm && (
                      <p className="text-xs text-gray-400" data-testid={`rate-source-label-${employee.id}`}>
                        {empRs === 'wage_table' ? 'Launatafla' : 'Sérsamin laun'}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-500">{empPm === 'hourly' ? 'Mánaðarlaun (reiknað)' : 'Mánaðarlaun'}</p>
                    <p className="font-medium" data-testid={`monthly-salary-label-${employee.id}`}>{kr(employee.monthly_salary)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Dagvinnutímakaup</p>
                    <p className="font-medium" data-testid={`day-rate-label-${employee.id}`}>{kr(employee.day_rate)}</p>
                    <p className="text-xs text-gray-400">Deilitala {String(employee.wage_divisor ?? 173.33).replace('.', ',')}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Yfirvinnukaup</p>
                    <p className="font-medium" data-testid={`overtime-rate-label-${employee.id}`}>{kr(employee.overtime_rate)}</p>
                    <p className="text-xs text-gray-400">
                      {(!employee.collective_agreement || employee.collective_agreement === 'sa_efling') ? 'SA/Efling' : employee.collective_agreement}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button
                    onClick={() => setContractEmployee(employee)}
                    variant="outline"
                    size="sm"
                    data-testid={`download-contract-${employee.id}`}
                  >
                    <FileDown className="mr-1 h-4 w-4" />
                    Samningur
                  </Button>
                  <Button
                    onClick={() => openAdvance(employee)}
                    variant="outline"
                    size="sm"
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    data-testid={`advance-payment-${employee.id}`}
                  >
                    <Banknote className="mr-1 h-4 w-4" />
                    Fyrirframgreiðsla
                  </Button>
                </div>
              </CardContent>
            </Card>
              );
            })}
        </div>

        {/* Fyrirframgreiðsla Dialog */}
        <Dialog open={advanceOpen} onOpenChange={setAdvanceOpen}>
          <DialogContent className="max-w-lg" aria-describedby="advance-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Banknote className="h-5 w-5 text-amber-600" />
                Fyrirframgreiðsla - {advanceEmployee?.full_name}
              </DialogTitle>
            </DialogHeader>
            <p id="advance-desc" className="sr-only">Skrá og skoða fyrirframgreiðslur</p>

            <form onSubmit={handleCreateAdvance} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">Upphæð (kr)</Label>
                  <Input
                    type="number"
                    value={advanceForm.amount}
                    onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })}
                    required
                    data-testid="advance-amount"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Dagsetning</Label>
                  <Input
                    type="date"
                    value={advanceForm.date}
                    onChange={(e) => setAdvanceForm({ ...advanceForm, date: e.target.value })}
                    required
                    data-testid="advance-date"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Athugasemd (valfrjálst)</Label>
                <Input
                  value={advanceForm.note}
                  onChange={(e) => setAdvanceForm({ ...advanceForm, note: e.target.value })}
                  placeholder="T.d. fyrirframgreiðsla launa"
                  data-testid="advance-note"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="advance-submit">
                Skrá greiðslu
              </Button>
            </form>

            {advancePayments.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <p className="text-sm font-semibold mb-2">Fyrri greiðslur</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {advancePayments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                      <div>
                        <span className="font-medium">{parseFloat(p.amount).toLocaleString('is-IS')} kr</span>
                        <span className="text-gray-500 ml-2">{p.date}</span>
                        {p.note && <span className="text-gray-400 ml-2">- {p.note}</span>}
                      </div>
                      <button
                        onClick={() => handleDeleteAdvance(p.id)}
                        className="text-red-400 hover:text-red-600 text-xs"
                        data-testid={`delete-advance-${p.id}`}
                      >
                        Eyða
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <ContractDialog
          employee={contractEmployee}
          open={!!contractEmployee}
          onClose={() => setContractEmployee(null)}
        />
      </div>
    </Layout>
  );
}
